import { inject, TransferState } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { PublisherModel } from '@app/pages/publisher/models/publisher-model';
import { PublisherAggrigatorService } from '@app/pages/publisher/services/publisher-aggrigator.service';
import { CustomerModel } from '@shared/model/customerModels/customer-model';
import { UserTypeEnum } from '@shared/model/enum/user-type-enum';

import { AuthorsAggrigatorService } from '@shared/service/http_services/authors-aggrigator.service';
import { CustomerAggrigatorService } from '@shared/service/http_services/customerAggrigator.service';
import { FollowsService } from '@shared/service/http_services/follows.service';
import { concatMap, forkJoin, map, Observable, of, tap } from 'rxjs';
import { ProfileUtilitiesService } from '../services/profile-utilities.service';
import { USER_PROFILE_ANALYZE_DATA, USER_PROFILE_MAIN_DATA, USER_PROFILE_STATISTICS_DATA } from 'constants/transfer-state-keys';
import { LocalRepository } from '@shared/helper/local-repository';
import { StatisticsAggrigatorService } from '@shared/service/http_services/statistics-aggrigator.service';
import { UserStateService } from '@app/state/user-state.service';

export const profileResolver: ResolveFn<Observable<boolean>> = (route) => {

  const repo = inject(LocalRepository);
  const userStateService = inject(UserStateService);
  const transferState = inject(TransferState);
  const customerAggrigatorService = inject(CustomerAggrigatorService);
  const authorsAggrigatorService = inject(AuthorsAggrigatorService);
  const publisherAggrigatorService = inject(PublisherAggrigatorService);
  const statisticAggregatorService = inject(StatisticsAggrigatorService);
  const followService = inject(FollowsService);
  const profileUtils = inject(ProfileUtilitiesService);
  const parameterIsId = route.paramMap.get('id')?.includes('-');
  const _userNameParameter = parameterIsId ? null : (route.paramMap.get('id') || '');
  const _userIdParameter = parameterIsId ? (route.paramMap.get('id') || '') : null;
  let _publisherData: PublisherModel | null = new PublisherModel();
  let _isUserProfile = false;
  let _isAuthorProfile = false;
  let _isPublisherProfile = false;


  const analyzeProfile = (): Observable<any> => {
    return customerAggrigatorService.transFormProfileId(_userNameParameter! || _userIdParameter!).pipe(
      tap(res => {
        transferState.set(USER_PROFILE_ANALYZE_DATA, res);
        const hasError = res.hasError;
        const userType = res?.result?.type;
        const id = res?.result?.id;
        if (_userIdParameter) {
          if (hasError) {
            _isUserProfile = false;
            _isAuthorProfile = !_isPublisherProfile;
          }
          else {
            _isUserProfile = true;
            _isAuthorProfile = (userType === UserTypeEnum.Author);
            if (userType === UserTypeEnum.Publisher) {
              _isPublisherProfile = true;
              _publisherData!.id = _userIdParameter;
            }
          }
        }
        else if (_userNameParameter) {
          _isUserProfile = true;
          if (hasError) {
            _isAuthorProfile = false;
            _isPublisherProfile = false;
          }
          else {
            _isAuthorProfile = (userType === UserTypeEnum.Author);
            if (userType === UserTypeEnum.Publisher) {
              _isPublisherProfile = true;
              _publisherData!.id = id;
            }
          }
        }
      })
    );
  }


  const getProfileMainData = (): Observable<CustomerModel | null> => {
    let stream$: Observable<CustomerModel | null>;
    if (_isUserProfile)
      stream$ = getUserMainData();
    else
      stream$ = _isAuthorProfile ? getAuthorMainData() : getPublisherMainData();
    return stream$;
  }

  const getUserMainData = (): Observable<CustomerModel> => {
    if (userStateService.userDataSnapshot) {
      return customerAggrigatorService.getCustomer(_userNameParameter!).pipe(map(res => res.result));
    }
    else
      return customerAggrigatorService.getGuestCustomer(_userNameParameter!).pipe(map(res => res.result));
  }

  const getAuthorMainData = (): Observable<CustomerModel> => {
    if (userStateService.userDataSnapshot)
      return authorsAggrigatorService.getAuthorById(_userIdParameter!).pipe(map(res => res.result));
    else
      return authorsAggrigatorService.getGuestAuthorById(_userIdParameter!).pipe(map(res => res.result));
  }

  const getPublisherMainData = (): Observable<CustomerModel | null> => {
    if (_isPublisherProfile && !_publisherData!.title) {
      return forkJoin({
        publisherData: publisherAggrigatorService.getGuestPublisherById(_publisherData?.id!),
        followState: followService.getFollowsState(_userIdParameter!)
      }).pipe(
        tap(data => _publisherData = data.publisherData.result),
        map(() => profileUtils.AdaptPublisherToCustomerModel(_publisherData!))
      );
    }
    else
      return of(null);
  }

  const getStatistics = (): Observable<any> => {
    return statisticAggregatorService.getGuestProfileStatistics(_userNameParameter! || _userIdParameter!).pipe(
      tap(res => {
        if (!res.hasError) {
          transferState.set(USER_PROFILE_STATISTICS_DATA, res);
        }
      })
    );
  }

  // ? main data and statistics are stored in transfer state
  return repo.IsInBrowser ? of(true) : analyzeProfile().pipe(
    concatMap(() => getProfileMainData()),
    tap(res => {
      if(res?.id) {
        transferState.set(USER_PROFILE_MAIN_DATA, res);
      }
      else {
        transferState.set(USER_PROFILE_MAIN_DATA, 'not-found');
      }
    }),
    concatMap(() => getStatistics()),
    map(() => true)
  ) as Observable<boolean>;

};